import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  IconButton,
  Paper,
  Box,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Card,
  CardContent,
  ListItemSecondaryAction,
  ListItemIcon,
  Tabs,
  Tab,
  Avatar,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  Stack,
  CircularProgress,
  LinearProgress,
  Tooltip as MuiTooltip,
  Snackbar,
  Alert,
  Checkbox,
  Collapse,
  alpha,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import SchoolIcon from '@mui/icons-material/School';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CampaignIcon from '@mui/icons-material/Campaign';
import BarChartIcon from '@mui/icons-material/BarChart';
import ListAlt from '@mui/icons-material/ListAlt';
import InfoIcon from '@mui/icons-material/Info';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import CakeIcon from '@mui/icons-material/Cake';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotesIcon from '@mui/icons-material/Notes';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import LanguageIcon from '@mui/icons-material/Language';
import WcIcon from '@mui/icons-material/Wc';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import Layout from '../components/Layout';
import SendNotificationDialog from '../components/SendNotificationDialog';
import ImportStudentsDialog from '../components/ImportStudentsDialog';
import AlfaSyncDialog from '../components/AlfaSyncDialog';
import AlfaReviewPanel from '../components/AlfaReviewPanel';
import TelegramConfigDialog from '../components/TelegramConfigDialog';
import TelegramIcon from '@mui/icons-material/Telegram';
import useStore from '../store/useStore';
import { translations } from '../translations';
import DashboardHero from '../components/dashboard/DashboardHero';
import BirthdayBoard from '../components/dashboard/BirthdayBoard';
import LearnCard from '../components/learn/LearnCard';
import StatTile from '../components/dashboard/StatTile';
import { useStaggerReveal } from '../components/fx/motion';
import { orchard } from '../theme';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

// ── AlfaCRM metadata presentation ─────────────────────────────────────────
const ALFA_STUDY_STATUS = {
  1: 'Учится',
  2: 'Приостановлено',
  3: 'Не учится',
  4: 'Вып. програм.',
};
const ALFA_LEGAL_TYPE = { 1: 'Физическое лицо', 2: 'Юридическое лицо' };
const ALFA_LEAD_SOURCE = {
  1: 'Сайт',
  2: 'Друзья',
  3: 'Реклама',
  4: 'Соцсети',
  5: 'Поиск',
  6: 'Родители',
  7: 'Другое',
  8: 'Сарафан',
};
const ALFA_GENDER = { 1: 'М', 2: 'Ж' };

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v);
  const d = new Date(
    s.includes('T') || s.includes(' ') ? s.replace(' ', 'T') : s
  );
  if (isNaN(d.getTime())) return s;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}
function fmtDateTime(v) {
  if (!v) return '—';
  const d = new Date(String(v).replace(' ', 'T'));
  if (isNaN(d.getTime())) return String(v);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0,
  }).format(n);
}
function fmtAge(dob) {
  if (!dob) return null;
  const d = new Date(
    String(dob).split(/[ T]/)[0].split('.').reverse().join('-')
  );
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const mo = now.getMonth() - d.getMonth();
  if (mo < 0 || (mo === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

// key → { label, icon, group, render }
const ALFA_META = {
  // Contact
  phone: { label: 'Телефон', icon: PhoneIcon, group: 'contact' },
  email: { label: 'Email', icon: EmailIcon, group: 'contact' },
  addr: { label: 'Адрес', icon: LocationOnIcon, group: 'contact' },
  web: { label: 'Сайт', icon: LanguageIcon, group: 'contact' },

  // Personal
  gender: {
    label: 'Пол',
    icon: WcIcon,
    group: 'personal',
    render: (v) => ALFA_GENDER[v] || String(v),
  },
  dob: {
    label: 'Дата рождения',
    icon: CakeIcon,
    group: 'personal',
    render: (v) => {
      const a = fmtAge(v);
      return a != null ? `${fmtDate(v)} · ${a} лет` : fmtDate(v);
    },
  },
  legal_name: {
    label: 'Законный представитель',
    icon: PersonIcon,
    group: 'personal',
  },
  legal_type: {
    label: 'Тип плательщика',
    icon: PersonIcon,
    group: 'personal',
    render: (v) => ALFA_LEGAL_TYPE[v] || String(v),
  },

  // Status
  is_study: {
    label: 'Активен',
    icon: CheckCircleIcon,
    group: 'status',
    render: (v) => (Number(v) ? 'Да' : 'Нет'),
    highlight: (v) => (Number(v) ? 'ok' : 'warn'),
  },
  study_status_id: {
    label: 'Статус обучения',
    icon: SchoolIcon,
    group: 'status',
    render: (v) => ALFA_STUDY_STATUS[v] || `Статус ${v}`,
  },
  pipeline_id: {
    label: 'Воронка',
    icon: ListAlt,
    group: 'status',
    render: (v) => `Этап ${v}`,
  },
  lead_source_id: {
    label: 'Источник',
    icon: CampaignIcon,
    group: 'status',
    render: (v) => ALFA_LEAD_SOURCE[v] || `Источник ${v}`,
  },

  // Financial
  balance: {
    label: 'Баланс',
    icon: AccountBalanceWalletIcon,
    group: 'finance',
    render: fmtMoney,
    highlight: (v) =>
      Number(v) > 0 ? 'ok' : Number(v) < 0 ? 'bad' : 'neutral',
  },
  balance_base: {
    label: 'Осн. баланс',
    icon: AccountBalanceWalletIcon,
    group: 'finance',
    render: fmtMoney,
  },
  balance_bonus: {
    label: 'Бонусы',
    icon: AccountBalanceWalletIcon,
    group: 'finance',
    render: fmtMoney,
  },
  paid_count: {
    label: 'Оплачено занятий',
    icon: AccountBalanceWalletIcon,
    group: 'finance',
  },
  paid_lesson_count: {
    label: 'Остаток занятий',
    icon: AccountBalanceWalletIcon,
    group: 'finance',
  },

  // Dates
  next_lesson_date: {
    label: 'След. занятие',
    icon: EventIcon,
    group: 'dates',
    render: fmtDateTime,
    highlight: () => 'ok',
  },
  last_attend_date: {
    label: 'Последнее посещение',
    icon: EventIcon,
    group: 'dates',
    render: fmtDate,
  },
  b_date: {
    label: 'Начало',
    icon: EventIcon,
    group: 'dates',
    render: fmtDateTime,
  },
  e_date: {
    label: 'Окончание',
    icon: EventIcon,
    group: 'dates',
    render: fmtDate,
  },
  created_at: {
    label: 'Создан',
    icon: EventIcon,
    group: 'dates',
    render: fmtDateTime,
  },
  updated_at: {
    label: 'Обновлён',
    icon: EventIcon,
    group: 'dates',
    render: fmtDateTime,
  },

  // System
  id: { label: 'ID в AlfaCRM', icon: FingerprintIcon, group: 'system' },
  alfaId: { label: 'ID в AlfaCRM', icon: FingerprintIcon, group: 'system' },
  source: { label: 'Источник данных', icon: CloudSyncIcon, group: 'system' },
  branch_ids: {
    label: 'Филиалы',
    icon: LocationOnIcon,
    group: 'system',
    render: (v) => (Array.isArray(v) ? v.join(', ') : String(v)),
  },
  teacher_ids: {
    label: 'Преподаватели',
    icon: PersonIcon,
    group: 'system',
    render: (v) => (Array.isArray(v) ? v.join(', ') : String(v)),
  },

  // Notes
  note: { label: 'Заметка', icon: NotesIcon, group: 'note' },
  name: { label: 'Имя', icon: PersonIcon, group: 'personal' },
};

const ALFA_GROUP_META = {
  contact: { title: 'Контакты', color: '#0891b2' },
  personal: { title: 'Данные', color: '#7c3aed' },
  status: { title: 'Статус', color: '#16a34a' },
  finance: { title: 'Финансы', color: '#d97706' },
  dates: { title: 'Даты', color: '#2563eb' },
  note: { title: 'Заметки', color: '#475569' },
  system: { title: 'Системные', color: '#94a3b8' },
  other: { title: 'Прочее', color: '#64748b' },
};

function AlfaMetaCard({ metadata }) {
  const [showRaw, setShowRaw] = React.useState(false);
  const entries = Object.entries(metadata || {}).filter(
    ([, v]) =>
      v !== null &&
      v !== undefined &&
      v !== '' &&
      (!Array.isArray(v) || v.length)
  );
  if (!entries.length) return null;

  const byGroup = {};
  entries.forEach(([key, value]) => {
    const cfg = ALFA_META[key];
    const group = cfg ? cfg.group : 'other';
    (byGroup[group] = byGroup[group] || []).push({ key, value, cfg });
  });

  // De-dup: if both id and alfaId present with same value, drop id.
  if (byGroup.system) {
    const seenVals = new Set();
    byGroup.system = byGroup.system.filter(({ key, value }) => {
      if ((key === 'id' || key === 'alfaId') && seenVals.has(String(value)))
        return false;
      seenVals.add(String(value));
      return true;
    });
  }

  const order = [
    'personal',
    'contact',
    'status',
    'finance',
    'dates',
    'note',
    'system',
    'other',
  ];

  const renderValue = ({ value, cfg, key }) => {
    if (cfg && cfg.render) {
      try {
        return cfg.render(value);
      } catch {
        return String(value);
      }
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const highlightColor = (h) => {
    if (h === 'ok') return '#16a34a';
    if (h === 'warn') return '#d97706';
    if (h === 'bad') return '#dc2626';
    return null;
  };

  return (
    <Box>
      {order
        .filter((g) => byGroup[g] && byGroup[g].length)
        .map((g) => {
          const meta = ALFA_GROUP_META[g] || ALFA_GROUP_META.other;
          return (
            <Box key={g} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 0.6,
                  color: meta.color,
                  fontSize: '0.68rem',
                  display: 'block',
                  mb: 0.75,
                }}
              >
                {meta.title}
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 0.75,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  p: 1,
                  bgcolor: (theme) => alpha(meta.color, 0.04),
                }}
              >
                {byGroup[g].map(({ key, value, cfg }) => {
                  const Icon = cfg && cfg.icon ? cfg.icon : InfoIcon;
                  const display = renderValue({ value, cfg, key });
                  const hc =
                    cfg && cfg.highlight
                      ? highlightColor(cfg.highlight(value))
                      : null;
                  const isLink =
                    key === 'email'
                      ? `mailto:${value}`
                      : key === 'phone'
                        ? `tel:${String(value).replace(/[^\d+]/g, '')}`
                        : key === 'web'
                          ? String(value).startsWith('http')
                            ? value
                            : `https://${value}`
                          : null;
                  return (
                    <Box
                      key={key}
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 0.75,
                        borderRadius: 1,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        gridColumn:
                          key === 'note' ||
                          key === 'legal_name' ||
                          key === 'addr'
                            ? { sm: 'span 2' }
                            : 'auto',
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: 16,
                          color: meta.color,
                          mt: 0.2,
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            display: 'block',
                            lineHeight: 1.2,
                          }}
                        >
                          {cfg ? cfg.label : key}
                        </Typography>
                        {isLink ? (
                          <Typography
                            component="a"
                            href={isLink}
                            variant="body2"
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              color: hc || meta.color,
                              textDecoration: 'none',
                              wordBreak: 'break-word',
                              display: 'block',
                              '&:hover': { textDecoration: 'underline' },
                            }}
                          >
                            {display}
                          </Typography>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: '0.78rem',
                              fontWeight: 600,
                              color: hc || 'text.primary',
                              wordBreak: 'break-word',
                            }}
                          >
                            {display}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        })}

      <Button
        size="small"
        variant="text"
        onClick={() => setShowRaw((v) => !v)}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          color: 'text.secondary',
        }}
      >
        {showRaw ? 'Скрыть сырые данные' : 'Показать сырые данные'}
      </Button>
      <Collapse in={showRaw}>
        <Box
          component="pre"
          sx={{
            mt: 1,
            p: 1,
            fontSize: '0.65rem',
            fontFamily: 'ui-monospace, monospace',
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflowX: 'auto',
            maxHeight: 200,
          }}
        >
          {JSON.stringify(metadata, null, 2)}
        </Box>
      </Collapse>
    </Box>
  );
}

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`dashboard-tabpanel-${index}`}
      aria-labelledby={`dashboard-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminDashboard() {
  const [students, setStudents] = useState([]);
  const [name, setName] = useState('');
  const [parents, setParents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [openAssignParentDialog, setOpenAssignParentDialog] = useState(false);
  const [systemStats, setSystemStats] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [activityLogs, setActivityLogs] = useState([]);
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [users, setUsers] = useState({ teachers: [], parents: [] });

  const [editTeacher, setEditTeacher] = useState(null);
  const [editTeacherForm, setEditTeacherForm] = useState({
    username: '',
    displayName: '',
    email: '',
    password: '',
  });
  const [editTeacherMsg, setEditTeacherMsg] = useState({
    text: '',
    error: false,
  });
  const [editTeacherSaving, setEditTeacherSaving] = useState(false);
  const [openSendNotification, setOpenSendNotification] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [viewingStudent, setViewingStudent] = useState(null);

  // Edit Student State
  const [openEditStudent, setOpenEditStudent] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [editStudentData, setEditStudentData] = useState({
    name: '',
    age: '',
    gender: '',
    dateOfBirth: '',
  });

  // Edit Parent State
  const [openEditParent, setOpenEditParent] = useState(false);
  const [editingParent, setEditingParent] = useState(null);
  const [editParentData, setEditParentData] = useState({
    username: '',
    name: '',
    email: '',
    password: '',
  });

  // Search State
  const [studentSearch, setStudentSearch] = useState('');
  const [parentSearch, setParentSearch] = useState('');

  const [openAlfaDialog, setOpenAlfaDialog] = useState(false);
  const [alfaReviewSection, setAlfaReviewSection] = useState(0);
  const [openTelegramDialog, setOpenTelegramDialog] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    id: null,
    type: null,
  });
  const [deleteAllConfirm, setDeleteAllConfirm] = useState({
    open: false,
    type: null,
  });
  const [deleteAllInput, setDeleteAllInput] = useState('');
  const [logFilter, setLogFilter] = useState('');
  const [openAddTeacher, setOpenAddTeacher] = useState(false);
  const [newTeacherUsername, setNewTeacherUsername] = useState('');
  const [newTeacherPassword, setNewTeacherPassword] = useState('');
  const [newTeacherMsg, setNewTeacherMsg] = useState({
    text: '',
    error: false,
  });

  // Multi-select state
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [syncStatus, setSyncStatus] = useState(null); // { outOfSyncCount, syncableCount, orphanOnlyCount }
  const [syncingAll, setSyncingAll] = useState(false);
  const [bulkTeacherId, setBulkTeacherId] = useState('');

  // Parent Form State (Create)
  const [parentUsername, setParentUsername] = useState('');
  const [parentPassword, setParentPassword] = useState('');
  const [parentEmail, setParentEmail] = useState('');

  const { user, language, socket } = useStore();
  const t = translations[language];
  const navigate = useNavigate();
  const pageRef = useRef(null);
  useStaggerReveal(pageRef);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [
        studentsRes,
        parentsRes,
        statsRes,
        logsRes,
        notifyRes,
        usersRes,
        syncRes,
      ] = await Promise.all([
        fetch('/api/students', { credentials: 'include' }),
        fetch('/api/admin/parents', { credentials: 'include' }),
        fetch('/api/admin/stats', { credentials: 'include' }),
        fetch('/api/admin/activity-logs', { credentials: 'include' }),
        fetch('/api/admin/notifications/history', { credentials: 'include' }),
        fetch('/api/admin/users', { credentials: 'include' }),
        fetch('/api/profiles/sync-status', { credentials: 'include' }),
      ]);

      setStudents(await studentsRes.json());
      setParents(await parentsRes.json());
      setSystemStats(await statsRes.json());
      setActivityLogs(await logsRes.json());
      setNotificationHistory(await notifyRes.json());
      setUsers(await usersRes.json());
      setSyncStatus(syncRes.ok ? await syncRes.json() : null);
    } catch (err) {
      console.error('Error fetching admin data:', err);
    }
  };

  // One-click: sync every drifted profile (safe ops only — rename + add missing;
  // orphaned themes are left for manual per-profile review).
  const handleSyncAll = async () => {
    if (
      !window.confirm(
        'Sync all out-of-sync student profiles to their current templates?\n\n' +
          'This renames changed themes/items and adds new themes. It does NOT ' +
          'remove themes deleted from a template — those stay for manual review.'
      )
    )
      return;
    setSyncingAll(true);
    try {
      const res = await fetch('/api/profiles/sync-all', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Sync failed');
      const remaining = data.remainingOrphans
        ? ` ${data.remainingOrphans} still need manual review (removed themes).`
        : '';
      setSnack({
        open: true,
        message: `Synced ${data.synced} profile(s).${remaining}`,
        severity: 'success',
      });
      fetchData();
    } catch (err) {
      setSnack({
        open: true,
        message: err.message || 'Error syncing profiles',
        severity: 'error',
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleSyncParentNames = async () => {
    try {
      const res = await fetch('/api/admin/students/sync-parent-names', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      setSnack({ open: true, message: data.message, severity: 'success' });
      fetchData();
    } catch (err) {
      setSnack({
        open: true,
        message: 'Error syncing names',
        severity: 'error',
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Filtered Lists
  const filteredStudents = students.filter((s) =>
    s.name.toLowerCase().includes(studentSearch.toLowerCase())
  );

  const filteredParents = parents.filter(
    (p) =>
      (p.name || '').toLowerCase().includes(parentSearch.toLowerCase()) ||
      (p.username || '').toLowerCase().includes(parentSearch.toLowerCase()) ||
      (p.email || '').toLowerCase().includes(parentSearch.toLowerCase())
  );

  const handleUpdateStudent = async () => {
    try {
      const res = await fetch(`/api/admin/students/${editingStudent._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editStudentData),
        credentials: 'include',
      });
      if (res.ok) {
        setOpenEditStudent(false);
        fetchData();
      }
    } catch (err) {
      console.error('Error updating student:', err);
    }
  };

  const handleUpdateParent = async () => {
    try {
      const res = await fetch(`/api/admin/parents/${editingParent._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editParentData),
        credentials: 'include',
      });
      if (res.ok) {
        setOpenEditParent(false);
        fetchData();
      }
    } catch (err) {
      console.error('Error updating parent:', err);
    }
  };

  const handleAssignTeacher = async (studentId, teacherId) => {
    try {
      const res = await fetch(`/api/admin/students/${studentId}/teacher`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teacherId }),
        credentials: 'include',
      });
      if (res.ok) {
        fetchData();
      } else {
        alert('Failed to assign teacher');
      }
    } catch (err) {
      console.error('Error assigning teacher:', err);
    }
  };

  async function addStudent(e) {
    e.preventDefault();
    if (!name.trim()) return;
    const res = await fetch('/api/students', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: name.trim() }),
    });
    if (res.ok) {
      setName('');
      fetchData();
    }
  }

  async function addParent(e) {
    e.preventDefault();
    const res = await fetch('/api/admin/parents', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: parentUsername,
        password: parentPassword,
        email: parentEmail,
      }),
    });
    if (res.ok) {
      setParentUsername('');
      setParentPassword('');
      setParentEmail('');
      fetchData();
    }
  }

  async function toggleParentAssignment(studentId, parentId, isAssigned) {
    const student = students.find((s) => s._id === studentId);
    let newParentIds = student.parentIds.map((p) => p._id || p);

    if (isAssigned) {
      newParentIds = newParentIds.filter((id) => id !== parentId);
    } else {
      newParentIds.push(parentId);
    }

    const res = await fetch(`/api/admin/students/${studentId}/parents`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ parentIds: newParentIds }),
    });

    if (res.ok) {
      fetchData();
    }
  }

  async function deleteAccount(userId, userType) {
    setDeleteConfirm({ open: true, id: userId, type: userType });
  }

  async function doDeleteAccount() {
    const { id, type } = deleteConfirm;
    setDeleteConfirm({ open: false, id: null, type: null });
    const res = await fetch(`/api/admin/users/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userType: type }),
      credentials: 'include',
    });
    if (res.ok) {
      setSnack({ open: true, message: t.delete + ' OK', severity: 'success' });
      fetchData();
    } else {
      setSnack({ open: true, message: 'Error', severity: 'error' });
    }
  }

  async function deleteAll(type) {
    setDeleteAllInput('');
    setDeleteAllConfirm({ open: true, type });
  }

  async function doDeleteAll() {
    if (deleteAllInput !== 'DELETE') return;
    const { type } = deleteAllConfirm;
    setDeleteAllConfirm({ open: false, type: null });
    setDeleteAllInput('');
    const res = await fetch(`/api/admin/${type}/all`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (res.ok) {
      setSnack({
        open: true,
        message: t.deleteAll + ' OK',
        severity: 'success',
      });
      fetchData();
    } else {
      setSnack({ open: true, message: 'Error', severity: 'error' });
    }
  }

  const handleAddTeacher = async () => {
    if (!newTeacherUsername.trim() || !newTeacherPassword.trim()) return;
    setNewTeacherMsg({ text: '', error: false });
    try {
      const res = await fetch('/api/admin/teachers', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: newTeacherUsername.trim(),
          password: newTeacherPassword,
        }),
      });
      if (res.ok) {
        setNewTeacherUsername('');
        setNewTeacherPassword('');
        setOpenAddTeacher(false);
        setSnack({
          open: true,
          message: t.addNewTeacher + ' — OK',
          severity: 'success',
        });
        fetchData();
      } else {
        const err = await res.json();
        setNewTeacherMsg({ text: err.message || 'Error', error: true });
      }
    } catch (err) {
      setNewTeacherMsg({ text: 'Error', error: true });
    }
  };

  const handleBulkAssignTeacher = async () => {
    if (!bulkTeacherId || selectedStudentIds.size === 0) return;
    await Promise.all(
      [...selectedStudentIds].map((id) =>
        handleAssignTeacher(id, bulkTeacherId)
      )
    );
    setSelectedStudentIds(new Set());
    setBulkTeacherId('');
  };

  const toggleSelectStudent = (id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedStudentIds.size === filteredStudents.length) {
      setSelectedStudentIds(new Set());
    } else {
      setSelectedStudentIds(new Set(filteredStudents.map((s) => s._id)));
    }
  };

  return (
    <Layout title={t.admin}>
      <Container maxWidth="xl" ref={pageRef}>
        {/* Hero Section */}
        <DashboardHero
          eyebrow={language === 'ru' ? 'Администратор' : 'Administrator'}
          title={`${t.welcome}, ${user?.username}!`}
          subtitle={
            language === 'ru'
              ? 'Управляйте школой, следите за результатами и общайтесь с родителями.'
              : 'Manage your school system, track performance, and communicate with parents.'
          }
          actions={
            <Button
              variant="contained"
              size="large"
              disableElevation
              fullWidth
              sx={{
                bgcolor: '#ffffff',
                // theme gives contained buttons a cherry gradient image
                backgroundImage: 'none',
                color: orchard.cherryDeep,
                fontWeight: 800,
                px: 4,
                '&:hover': {
                  bgcolor: orchard.blossom,
                  backgroundImage: 'none',
                },
              }}
              onClick={() => setOpenSendNotification(true)}
              startIcon={<CampaignIcon />}
            >
              {t.broadcastUpdate}
            </Button>
          }
        />

        <LearnCard />
        <BirthdayBoard all en={language === 'en'} />

        {/* Out-of-sync alert: one click syncs every drifted profile */}
        {syncStatus && syncStatus.outOfSyncCount > 0 && (
          <Alert
            severity="warning"
            variant="outlined"
            icon={<CloudSyncIcon />}
            sx={{
              mb: 4,
              borderRadius: 3,
              alignItems: 'center',
              '& .MuiAlert-message': { flexGrow: 1 },
            }}
            action={
              <Button
                color="warning"
                variant="contained"
                size="large"
                disableElevation
                disabled={syncingAll || syncStatus.syncableCount === 0}
                startIcon={
                  syncingAll ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <CloudSyncIcon />
                  )
                }
                onClick={handleSyncAll}
                sx={{ fontWeight: 800, whiteSpace: 'nowrap' }}
              >
                {syncingAll
                  ? 'Syncing…'
                  : `Sync all (${syncStatus.syncableCount})`}
              </Button>
            }
          >
            <Typography variant="subtitle1" fontWeight={800}>
              {syncStatus.outOfSyncCount} student profile
              {syncStatus.outOfSyncCount === 1 ? ' is' : 's are'} out of sync
              with their templates
            </Typography>
            <Typography variant="body2" color="text.secondary">
              One click renames changed themes/items and adds new themes across
              every profile.
              {syncStatus.orphanOnlyCount > 0 &&
                ` ${syncStatus.orphanOnlyCount} also have removed themes — review those individually.`}
            </Typography>
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            <Tab
              icon={<BarChartIcon />}
              iconPosition="start"
              label={t.analytics}
            />
            <Tab
              icon={<SchoolIcon />}
              iconPosition="start"
              label={t.studentDirectory}
            />
            <Tab
              icon={<SupervisorAccountIcon />}
              iconPosition="start"
              label={t.parentDirectory}
            />
            <Tab
              icon={<SupervisorAccountIcon />}
              iconPosition="start"
              label={t.staff}
            />
            <Tab
              icon={<CampaignIcon />}
              iconPosition="start"
              label={t.communication}
            />
            <Tab icon={<ListAlt />} iconPosition="start" label={t.logs} />
            <Tab
              icon={<CloudSyncIcon />}
              iconPosition="start"
              label={
                language === 'ru'
                  ? 'AlfaCRM: проверка и объединение'
                  : 'AlfaCRM: review & merge'
              }
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {systemStats && (
            <>
              {/* Core counts */}
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                  {
                    key: 'totalStudents',
                    label: t.studentDirectory,
                    color: '#C2233E',
                    icon: <PersonIcon />,
                  },
                  {
                    key: 'totalTeachers',
                    label: t.staff,
                    color: '#2E7D5B',
                    icon: <SchoolIcon />,
                  },
                  {
                    key: 'totalParents',
                    label: t.parentDirectory,
                    color: '#7C5CBF',
                    icon: <SupervisorAccountIcon />,
                  },
                  {
                    key: 'totalProfiles',
                    label: t.assessments,
                    color: '#0891B2',
                    icon: <BarChartIcon />,
                  },
                  {
                    key: 'assessmentsThisWeek',
                    label: t.assessmentsWeek,
                    color: '#E8A93D',
                    icon: <EventIcon />,
                  },
                  {
                    key: 'avgMastery',
                    label: t.avgMastery,
                    color: '#8C1230',
                    icon: <CheckCircleIcon />,
                    suffix: '%',
                  },
                ].map(({ key, label, color, icon, suffix = '' }, i) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2 }} key={key}>
                    <StatTile
                      revealAttr="data-reveal"
                      icon={icon}
                      label={label}
                      value={Number(systemStats[key]) || 0}
                      suffix={suffix}
                      color={color}
                      delay={i * 0.06}
                    />
                  </Grid>
                ))}
              </Grid>

              {/* Data health */}
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, display: 'block', mb: 1 }}
              >
                {t.dataHealth}
              </Typography>
              <Grid container spacing={1.5} sx={{ mb: 3 }}>
                {[
                  { key: 'studentsWithNoTeacher', label: t.noTeacher },
                  { key: 'studentsWithNoParent', label: t.noParent },
                  { key: 'studentsWithNoProfile', label: t.neverAssessed },
                ].map(({ key, label }) => {
                  const warn = systemStats[key] > 0;
                  return (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={key}>
                      <StatTile
                        revealAttr="data-reveal"
                        icon={warn ? <WarningAmberIcon /> : <CheckCircleIcon />}
                        label={label}
                        value={Number(systemStats[key]) || 0}
                        color={warn ? '#E8A93D' : '#2E7D5B'}
                      />
                    </Grid>
                  );
                })}
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <Card sx={{ borderRadius: 4, height: '100%' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontWeight: 700 }}
                      >
                        {t.lastSync}
                      </Typography>
                      <Typography variant="body2">
                        {systemStats.lastSync
                          ? new Date(systemStats.lastSync).toLocaleString()
                          : t.neverLabel}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Teacher breakdown */}
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, display: 'block', mb: 1 }}
              >
                {t.teacherOverview}
              </Typography>
              <Paper variant="outlined" sx={{ borderRadius: 3, mb: 3 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t.userCol}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          {t.studentDirectory}
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700 }}>
                          {t.groupsLabel}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          {t.lastAssessmentLabel}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(systemStats.teacherBreakdown || []).map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {row.name}
                          </TableCell>
                          <TableCell align="center">
                            {row.studentCount}
                          </TableCell>
                          <TableCell align="center">{row.groupCount}</TableCell>
                          <TableCell>
                            {row.lastAssessment ? (
                              new Date(row.lastAssessment).toLocaleDateString()
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.disabled"
                              >
                                {t.neverLabel}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              {/* Quick navigation */}
              <Typography
                variant="overline"
                color="text.secondary"
                sx={{ fontWeight: 700, display: 'block', mb: 1 }}
              >
                {t.quickNav}
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  { label: t.templates, onClick: () => navigate('/templates') },
                  { label: t.analytics, onClick: () => navigate('/analytics') },
                  { label: t.staff, onClick: () => setTabValue(3) },
                  { label: t.studentDirectory, onClick: () => setTabValue(1) },
                  {
                    label: t.syncParentNamesBtn,
                    onClick: handleSyncParentNames,
                  },
                ].map(({ label, onClick }) => (
                  <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={label}>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={onClick}
                      sx={{ py: 1.5 }}
                    >
                      {label}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t.studentDirectory}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                <TextField
                  size="small"
                  placeholder={t.searchLabel}
                  value={studentSearch}
                  onChange={(e) => setStudentSearch(e.target.value)}
                  sx={{ flexGrow: { xs: 1, md: 0 }, width: { md: 200 } }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenAlfaDialog(true)}
                  startIcon={<CloudSyncIcon />}
                  color="info"
                >
                  {t.syncBtn}
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadFileIcon />}
                  onClick={() => setOpenImportDialog(true)}
                >
                  {t.importBtn}
                </Button>
              </Box>

              <Box
                component="form"
                onSubmit={addStudent}
                sx={{
                  display: 'flex',
                  gap: 1,
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <TextField
                  size="small"
                  placeholder={t.addStudent}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  sx={{ flexGrow: 1 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                >
                  {t.create}
                </Button>
              </Box>
            </Box>

            {/* Bulk action bar */}
            <Collapse in={selectedStudentIds.size > 0}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha('#C2233E', 0.08),
                  border: '1px solid',
                  borderColor: alpha('#C2233E', 0.25),
                  flexWrap: 'wrap',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: 'primary.main', minWidth: 80 }}
                >
                  {selectedStudentIds.size}{' '}
                  {language === 'ru' ? 'выбрано' : 'selected'}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 180 }}>
                  <Select
                    value={bulkTeacherId}
                    onChange={(e) => setBulkTeacherId(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>{t.assignTeacher}</em>
                    </MenuItem>
                    {users.teachers.map((tc) => (
                      <MenuItem key={tc._id} value={tc._id}>
                        {tc.displayName || tc.username}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  size="small"
                  disabled={!bulkTeacherId}
                  onClick={handleBulkAssignTeacher}
                >
                  {t.assignTeacher}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setSelectedStudentIds(new Set())}
                >
                  {t.cancel}
                </Button>
              </Box>
            </Collapse>

            {/* Select all row */}
            <Box sx={{ display: 'flex', alignItems: 'center', px: 1, mb: 0.5 }}>
              <Checkbox
                size="small"
                indeterminate={
                  selectedStudentIds.size > 0 &&
                  selectedStudentIds.size < filteredStudents.length
                }
                checked={
                  filteredStudents.length > 0 &&
                  selectedStudentIds.size === filteredStudents.length
                }
                onChange={toggleSelectAll}
              />
              <Typography variant="caption" color="text.secondary">
                {language === 'ru' ? 'Выбрать все' : 'Select all'}
              </Typography>
            </Box>

            <List disablePadding>
              {filteredStudents.map((s) => {
                const isSelected = selectedStudentIds.has(s._id);
                return (
                  <ListItem
                    key={s._id}
                    divider
                    sx={{
                      py: 1.5,
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      gap: { xs: 1, sm: 0 },
                      bgcolor: isSelected ? alpha('#C2233E', 0.05) : undefined,
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => toggleSelectStudent(s._id)}
                      sx={{ mr: 0.5 }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        minWidth: 0,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: 'primary.main',
                          fontWeight: 800,
                          width: 36,
                          height: 36,
                          fontSize: '0.9rem',
                          mr: 1.5,
                          flexShrink: 0,
                        }}
                      >
                        {s.name[0]}
                      </Avatar>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {s.name}
                        </Typography>
                        <FormControl
                          size="small"
                          variant="standard"
                          sx={{ minWidth: 130 }}
                        >
                          <Select
                            value={s.teacherId?._id || s.teacherId || ''}
                            onChange={(e) =>
                              handleAssignTeacher(s._id, e.target.value)
                            }
                            displayEmpty
                            sx={{ fontSize: '0.72rem', fontWeight: 600 }}
                          >
                            <MenuItem value="">
                              <em>{t.assignTeacher}</em>
                            </MenuItem>
                            {users.teachers.map((tc) => (
                              <MenuItem key={tc._id} value={tc._id}>
                                {tc.displayName || tc.username}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{
                        flexShrink: 0,
                        width: { xs: '100%', sm: 'auto' },
                        justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                      }}
                    >
                      <MuiTooltip title={t.editStudentTitle}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingStudent(s);
                            setEditStudentData({
                              name: s.name,
                              age: s.age || '',
                              gender: s.gender || '',
                              dateOfBirth: s.dateOfBirth
                                ? new Date(s.dateOfBirth)
                                    .toISOString()
                                    .split('T')[0]
                                : '',
                            });
                            setOpenEditStudent(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </MuiTooltip>
                      <MuiTooltip title={t.studentInfo}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setViewingStudent(s);
                            setOpenDetailsDialog(true);
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </MuiTooltip>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ minWidth: 'auto', px: 1, fontSize: '0.72rem' }}
                        onClick={() => navigate(`/student/${s._id}`)}
                      >
                        {t.portfolio}
                      </Button>
                      <Button
                        variant="text"
                        size="small"
                        sx={{ minWidth: 'auto', px: 1, fontSize: '0.72rem' }}
                        onClick={() => {
                          setSelectedStudent(s);
                          setOpenAssignParentDialog(true);
                        }}
                      >
                        {t.parent}
                      </Button>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteAccount(s._id, 'student')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </ListItem>
                );
              })}
            </List>

            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Button
                color="error"
                size="small"
                onClick={() => deleteAll('students')}
              >
                {t.deleteAll} {t.studentDirectory}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t.parentDirectory}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <TextField
                  size="small"
                  placeholder={t.searchParents}
                  value={parentSearch}
                  onChange={(e) => setParentSearch(e.target.value)}
                  sx={{ width: 200 }}
                />

                <Box
                  component="form"
                  onSubmit={addParent}
                  sx={{ display: 'flex', gap: 1 }}
                >
                  <TextField
                    size="small"
                    placeholder="Name"
                    value={parentUsername}
                    onChange={(e) => setParentUsername(e.target.value)}
                    required
                  />
                  <TextField
                    size="small"
                    placeholder="Email"
                    value={parentEmail}
                    onChange={(e) => setParentEmail(e.target.value)}
                  />
                  <TextField
                    size="small"
                    placeholder="Password"
                    type="password"
                    value={parentPassword}
                    onChange={(e) => setParentPassword(e.target.value)}
                    required
                  />
                  <Button type="submit" variant="contained" size="small">
                    {t.create}
                  </Button>
                </Box>
              </Box>
            </Box>

            <List>
              {filteredParents.map((p) => (
                <ListItem key={p._id} divider sx={{ py: 1.5 }}>
                  <ListItemIcon>
                    <Avatar sx={{ bgcolor: 'secondary.main' }}>
                      <SupervisorAccountIcon />
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {p.name || p.username}
                      </Typography>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          Email: {p.email || p.username}
                        </Typography>
                        {p.children && p.children.length > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: 'primary.main', fontWeight: 600 }}
                          >
                            {t.kidsLabel}:{' '}
                            {p.children.map((c) => c.name).join(', ')}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Stack direction="row" spacing={1}>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditingParent(p);
                          setEditParentData({
                            username: p.username,
                            name: p.name || '',
                            email: p.email || '',
                            password: '',
                          });
                          setOpenEditParent(true);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => deleteAccount(p._id, 'parent')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>

            <Box sx={{ mt: 3, textAlign: 'right' }}>
              <Button
                color="error"
                size="small"
                onClick={() => deleteAll('parents')}
              >
                {t.deleteAll} {t.parentDirectory}
              </Button>
            </Box>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Staff header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t.staff}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {users.teachers.length}{' '}
                {language === 'ru'
                  ? 'учителей в системе'
                  : 'teachers in the system'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setNewTeacherUsername('');
                  setNewTeacherPassword('');
                  setNewTeacherMsg({ text: '', error: false });
                  setOpenAddTeacher(true);
                }}
              >
                {t.addNewTeacher}
              </Button>
            </Stack>
          </Box>

          {/* Teacher cards */}
          <Grid container spacing={2}>
            {users.teachers.map((teacher) => {
              const stats = (systemStats?.teacherBreakdown || []).find(
                (b) => b.id === teacher._id
              );
              const initials = (teacher.displayName || teacher.username)
                .split(' ')
                .map((w) => w[0])
                .slice(0, 2)
                .join('')
                .toUpperCase();
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={teacher._id}>
                  <Card
                    elevation={0}
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'box-shadow 0.2s',
                      '&:hover': { boxShadow: 4 },
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      {/* Top: avatar + name + role */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mb: 2,
                        }}
                      >
                        <Avatar
                          sx={{
                            bgcolor:
                              teacher.role === 'admin'
                                ? 'secondary.main'
                                : 'primary.main',
                            width: 52,
                            height: 52,
                            fontWeight: 800,
                            fontSize: '1.1rem',
                          }}
                        >
                          {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: 700,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {teacher.displayName || teacher.username}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              teacher.role === 'admin' ? t.admin : t.teacher
                            }
                            color={
                              teacher.role === 'admin' ? 'secondary' : 'primary'
                            }
                            sx={{ fontSize: '0.7rem', height: 20 }}
                          />
                        </Box>
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      {/* Stats row */}
                      <Grid container>
                        <Grid size={{ xs: 4 }} sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="h5"
                            sx={{ fontWeight: 800, color: 'primary.main' }}
                          >
                            {stats?.studentCount ?? '–'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t.myStudents}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4 }} sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {stats?.groupCount ?? '–'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t.groupsLabel}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 4 }} sx={{ textAlign: 'center' }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                          >
                            {stats?.lastAssessment
                              ? new Date(
                                  stats.lastAssessment
                                ).toLocaleDateString()
                              : '–'}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t.lastAssessmentLabel}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>

                    {/* Footer actions */}
                    <Box
                      sx={{
                        px: 2,
                        pb: 1.5,
                        pt: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderTop: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Button
                        size="small"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => {
                          setEditTeacher(teacher);
                          setEditTeacherForm({
                            username: teacher.username || '',
                            displayName: teacher.displayName || '',
                            email: teacher.email || '',
                            password: '',
                          });
                          setEditTeacherMsg({ text: '', error: false });
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        {language === 'ru' ? 'Изменить' : 'Edit'}
                      </Button>
                      <MuiTooltip title={t.delete}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={teacher.role === 'admin'}
                            onClick={() =>
                              deleteAccount(teacher._id, 'teacher')
                            }
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </MuiTooltip>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
            {users.teachers.length === 0 && (
              <Grid size={{ xs: 12 }}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 5,
                    borderRadius: 3,
                    textAlign: 'center',
                    borderStyle: 'dashed',
                  }}
                >
                  <PersonIcon
                    sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
                  />
                  <Typography color="text.secondary">
                    {language === 'ru'
                      ? 'Нет учителей. Добавьте первого.'
                      : 'No teachers yet. Add the first one.'}
                  </Typography>
                </Paper>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={4}>
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                <CampaignIcon
                  sx={{ fontSize: 60, color: 'primary.main', mb: 2 }}
                />
                <Typography variant="h5" gutterBottom>
                  {t.broadcastMessage}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => setOpenSendNotification(true)}
                >
                  {t.composeNew}
                </Button>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
                <TelegramIcon sx={{ fontSize: 60, color: '#229ED9', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Telegram
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Токен бота и chat ID учительской группы.
                </Typography>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<TelegramIcon />}
                  onClick={() => setOpenTelegramDialog(true)}
                >
                  Настроить
                </Button>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t.sentHistory}
                </Typography>
                <List sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {notificationHistory.map((n) => (
                    <ListItem key={n._id} divider>
                      <ListItemText
                        primary={n.title}
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" color="text.primary">
                              {n.message}
                            </Typography>
                            <Typography variant="caption" display="block">
                              {t.sentBy} {n.sender.username} → {n.channel} •{' '}
                              {new Date(n.createdAt).toLocaleString()}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={5}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {t.activityLog}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <TextField
                  size="small"
                  placeholder={t.searchLabel}
                  value={logFilter.startsWith('ACTION:') ? '' : logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  sx={{ width: 180 }}
                />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={
                      logFilter.startsWith('ACTION:')
                        ? logFilter.replace('ACTION:', '')
                        : ''
                    }
                    onChange={(e) =>
                      setLogFilter(
                        e.target.value ? `ACTION:${e.target.value}` : ''
                      )
                    }
                    displayEmpty
                  >
                    <MenuItem value="">{t.allActions}</MenuItem>
                    {[...new Set(activityLogs.map((l) => l.action))].map(
                      (a) => (
                        <MenuItem key={a} value={a}>
                          {a}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Box>
            </Box>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>{t.timeCol}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{t.userCol}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t.actionCol}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {t.detailsCol}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activityLogs
                    .filter((log) => {
                      if (!logFilter) return true;
                      if (logFilter.startsWith('ACTION:'))
                        return log.action === logFilter.replace('ACTION:', '');
                      const q = logFilter.toLowerCase();
                      return (
                        log.action.toLowerCase().includes(q) ||
                        log.performedBy.username.toLowerCase().includes(q) ||
                        (log.details || '').toLowerCase().includes(q)
                      );
                    })
                    .map((log) => (
                      <TableRow key={log._id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {new Date(log.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {log.performedBy.username}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {log.performedBy.role}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={log.action}
                            color={
                              log.action.includes('DELETE')
                                ? 'error'
                                : log.action.includes('CREATE') ||
                                    log.action.includes('REGISTER')
                                  ? 'success'
                                  : log.action.includes('IMPORT')
                                    ? 'info'
                                    : 'primary'
                            }
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 300 }}>
                          <Typography
                            variant="body2"
                            sx={{ wordBreak: 'break-word' }}
                          >
                            {log.details}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>

        <TabPanel value={tabValue} index={6}>
          <AlfaReviewPanel
            section={alfaReviewSection}
            onSectionChange={setAlfaReviewSection}
            onDataChanged={fetchData}
          />
        </TabPanel>

        {/* Add Teacher Dialog */}
        <Dialog
          open={openAddTeacher}
          onClose={() => setOpenAddTeacher(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700 }}>{t.addNewTeacher}</DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                fullWidth
                label={t.usernameLogin}
                value={newTeacherUsername}
                onChange={(e) => setNewTeacherUsername(e.target.value)}
                autoComplete="off"
              />
              <TextField
                fullWidth
                label={language === 'ru' ? 'Пароль' : 'Password'}
                type="password"
                value={newTeacherPassword}
                onChange={(e) => setNewTeacherPassword(e.target.value)}
                autoComplete="new-password"
              />
              {newTeacherMsg.text && (
                <Alert severity={newTeacherMsg.error ? 'error' : 'success'}>
                  {newTeacherMsg.text}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAddTeacher(false)}>{t.cancel}</Button>
            <Button
              onClick={handleAddTeacher}
              variant="contained"
              disabled={
                !newTeacherUsername.trim() || !newTeacherPassword.trim()
              }
            >
              {t.create}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit teacher inline — replaces the old "Open User Management" dialog */}
        <Dialog
          open={!!editTeacher}
          onClose={() => setEditTeacher(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {language === 'ru' ? 'Редактировать учителя' : 'Edit Teacher'}
            {editTeacher && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                {editTeacher.displayName || editTeacher.username}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                label={language === 'ru' ? 'Логин' : 'Username'}
                fullWidth
                value={editTeacherForm.username}
                onChange={(e) =>
                  setEditTeacherForm({
                    ...editTeacherForm,
                    username: e.target.value,
                  })
                }
              />
              <TextField
                label={language === 'ru' ? 'Отображаемое имя' : 'Display Name'}
                fullWidth
                value={editTeacherForm.displayName}
                onChange={(e) =>
                  setEditTeacherForm({
                    ...editTeacherForm,
                    displayName: e.target.value,
                  })
                }
                helperText={
                  language === 'ru'
                    ? 'Видно в отчётах и подписях'
                    : 'Visible on reports and signatures'
                }
              />
              <TextField
                label="Email"
                fullWidth
                value={editTeacherForm.email}
                onChange={(e) =>
                  setEditTeacherForm({
                    ...editTeacherForm,
                    email: e.target.value,
                  })
                }
              />
              <TextField
                label={language === 'ru' ? 'Новый пароль' : 'New Password'}
                fullWidth
                type="password"
                placeholder={
                  language === 'ru'
                    ? 'Оставьте пустым, чтобы не менять'
                    : 'Leave blank to keep current'
                }
                value={editTeacherForm.password}
                onChange={(e) =>
                  setEditTeacherForm({
                    ...editTeacherForm,
                    password: e.target.value,
                  })
                }
                autoComplete="new-password"
              />
              {editTeacherMsg.text && (
                <Alert severity={editTeacherMsg.error ? 'error' : 'success'}>
                  {editTeacherMsg.text}
                </Alert>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditTeacher(null)}>{t.cancel}</Button>
            <Button
              onClick={async () => {
                if (!editTeacher) return;
                setEditTeacherSaving(true);
                setEditTeacherMsg({ text: '', error: false });
                try {
                  const r = await fetch(
                    `/api/admin/teachers/${editTeacher._id}`,
                    {
                      method: 'PATCH',
                      credentials: 'include',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        username: editTeacherForm.username,
                        displayName: editTeacherForm.displayName,
                        email: editTeacherForm.email,
                        password: editTeacherForm.password || undefined,
                      }),
                    }
                  );
                  if (!r.ok) {
                    const err = await r.json().catch(() => ({}));
                    throw new Error(err.message || 'Update failed');
                  }
                  setEditTeacherMsg({
                    text: language === 'ru' ? 'Сохранено' : 'Saved',
                    error: false,
                  });
                  fetchData();
                  setTimeout(() => setEditTeacher(null), 500);
                } catch (e) {
                  setEditTeacherMsg({ text: e.message, error: true });
                } finally {
                  setEditTeacherSaving(false);
                }
              }}
              variant="contained"
              disabled={editTeacherSaving || !editTeacherForm.username.trim()}
            >
              {editTeacherSaving
                ? language === 'ru'
                  ? 'Сохраняем…'
                  : 'Saving…'
                : language === 'ru'
                  ? 'Сохранить'
                  : 'Save'}
            </Button>
          </DialogActions>
        </Dialog>
        <SendNotificationDialog
          open={openSendNotification}
          onClose={() => {
            setOpenSendNotification(false);
            fetchData();
          }}
        />
        <ImportStudentsDialog
          open={openImportDialog}
          onClose={() => {
            setOpenImportDialog(false);
            fetchData();
          }}
          onImportSuccess={fetchData}
        />

        <Dialog
          open={openAssignParentDialog}
          onClose={() => setOpenAssignParentDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t.manageParentsFor} {selectedStudent?.name}
          </DialogTitle>
          <DialogContent>
            <List>
              {parents.map((parent) => {
                const isAssigned = selectedStudent?.parentIds?.some(
                  (p) => (p._id || p) === parent._id
                );
                return (
                  <ListItem key={parent._id}>
                    <ListItemText
                      primary={parent.username}
                      secondary={parent.email}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        variant={isAssigned ? 'outlined' : 'contained'}
                        color={isAssigned ? 'error' : 'primary'}
                        size="small"
                        onClick={() =>
                          toggleParentAssignment(
                            selectedStudent._id,
                            parent._id,
                            isAssigned
                          )
                        }
                      >
                        {isAssigned ? t.unassign : t.assign}
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenAssignParentDialog(false)}>
              {t.done}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={openDetailsDialog}
          onClose={() => setOpenDetailsDialog(false)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            sx: { borderRadius: 4, overflow: 'hidden' },
          }}
        >
          {/* Gradient header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #161020 0%, #C2233E 100%)',
              px: 3,
              pt: 3,
              pb: 2.5,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                width: 56,
                height: 56,
                fontSize: '1.5rem',
                fontWeight: 900,
                bgcolor: alpha('#ffffff', 0.15),
                color: '#ffffff',
                border: '2px solid',
                borderColor: alpha('#ffffff', 0.3),
                flexShrink: 0,
              }}
            >
              {viewingStudent?.name?.[0]}
            </Avatar>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, color: '#ffffff', lineHeight: 1.2 }}
              >
                {viewingStudent?.name}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: alpha('#ffffff', 0.65) }}
              >
                {t.studentInfo}
              </Typography>
            </Box>
          </Box>

          <DialogContent sx={{ p: 2.5 }}>
            {/* Stat chips */}
            <Grid container spacing={1} sx={{ mb: 2 }}>
              {[
                {
                  label: t.ageLabel,
                  value: viewingStudent?.age || '—',
                },
                {
                  label: t.genderLabel,
                  value: viewingStudent?.gender || '—',
                },
                {
                  label: t.dobLabel,
                  value: viewingStudent?.dateOfBirth
                    ? new Date(viewingStudent.dateOfBirth).toLocaleDateString()
                    : '—',
                },
              ].map(({ label, value }) => (
                <Grid size={{ xs: 4 }} key={label}>
                  <Box
                    sx={{
                      textAlign: 'center',
                      p: 1,
                      borderRadius: 2,
                      bgcolor: alpha('#C2233E', 0.06),
                      border: '1px solid',
                      borderColor: alpha('#C2233E', 0.12),
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontSize: '0.65rem', mb: 0.25 }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700, fontSize: '0.8rem' }}
                    >
                      {value}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>

            {/* Linked parents */}
            {viewingStudent?.parentIds?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'block',
                    mb: 0.75,
                  }}
                >
                  {t.linkedParents}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {viewingStudent.parentIds.map((p, i) => (
                    <Chip
                      key={i}
                      size="small"
                      icon={<PersonIcon style={{ fontSize: 14 }} />}
                      label={p.name || p.username}
                      variant="outlined"
                      sx={{ fontSize: '0.72rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {/* Metadata — interpreted AlfaCRM fields */}
            {viewingStudent?.metadata &&
              Object.keys(viewingStudent.metadata).length > 0 && (
                <>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      display: 'block',
                      mb: 1,
                    }}
                  >
                    {t.extendedMeta}
                  </Typography>
                  <AlfaMetaCard metadata={viewingStudent.metadata} />
                </>
              )}
          </DialogContent>

          <DialogActions sx={{ px: 2.5, pb: 2, pt: 0, gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setOpenDetailsDialog(false);
                navigate(`/student/${viewingStudent?._id}`);
              }}
            >
              {t.portfolio}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenDetailsDialog(false)}
              sx={{ ml: 'auto' }}
            >
              {t.close}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Student Dialog */}
        <Dialog
          open={openEditStudent}
          onClose={() => setOpenEditStudent(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {t.editStudentTitle}: {editingStudent?.name}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <TextField
                fullWidth
                label={t.fullName}
                value={editStudentData.name}
                onChange={(e) =>
                  setEditStudentData({
                    ...editStudentData,
                    name: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                label={t.ageLabel}
                type="number"
                value={editStudentData.age}
                onChange={(e) =>
                  setEditStudentData({
                    ...editStudentData,
                    age: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                label={t.genderLabel}
                value={editStudentData.gender}
                onChange={(e) =>
                  setEditStudentData({
                    ...editStudentData,
                    gender: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                label={t.dobLabel}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={editStudentData.dateOfBirth}
                onChange={(e) =>
                  setEditStudentData({
                    ...editStudentData,
                    dateOfBirth: e.target.value,
                  })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditStudent(false)}>
              {t.cancel}
            </Button>
            <Button onClick={handleUpdateStudent} variant="contained">
              {t.updateStudent}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Parent Dialog */}
        <Dialog
          open={openEditParent}
          onClose={() => setOpenEditParent(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>
            {t.editParentTitle}: {editingParent?.username}
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Typography variant="caption" color="primary">
                {t.credentialsLabel}
              </Typography>
              <TextField
                fullWidth
                label={t.usernameLogin}
                value={editParentData.username}
                onChange={(e) =>
                  setEditParentData({
                    ...editParentData,
                    username: e.target.value,
                  })
                }
              />
              <TextField
                fullWidth
                label="New Password (leave blank to keep current)"
                type="password"
                value={editParentData.password}
                onChange={(e) =>
                  setEditParentData({
                    ...editParentData,
                    password: e.target.value,
                  })
                }
              />
              <Divider />
              <Typography variant="caption" color="primary">
                Profile Information
              </Typography>
              <TextField
                fullWidth
                label="Display Name"
                value={editParentData.name}
                onChange={(e) =>
                  setEditParentData({ ...editParentData, name: e.target.value })
                }
              />
              <TextField
                fullWidth
                label="Email"
                value={editParentData.email}
                onChange={(e) =>
                  setEditParentData({
                    ...editParentData,
                    email: e.target.value,
                  })
                }
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenEditParent(false)}>Cancel</Button>
            <Button onClick={handleUpdateParent} variant="contained">
              Update Credentials
            </Button>
          </DialogActions>
        </Dialog>

        {/* AlfaCRM Sync Dialog */}
        <AlfaSyncDialog
          open={openAlfaDialog}
          onClose={() => setOpenAlfaDialog(false)}
          onSyncSuccess={fetchData}
          onReviewChanges={() => {
            setAlfaReviewSection(1);
            setTabValue(6);
          }}
        />

        {/* Telegram Config Dialog */}
        <TelegramConfigDialog
          open={openTelegramDialog}
          onClose={() => setOpenTelegramDialog(false)}
        />

        {/* Delete single item confirmation */}
        <Dialog
          open={deleteConfirm.open}
          onClose={() =>
            setDeleteConfirm({ open: false, id: null, type: null })
          }
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t.confirmDeleteTitle}</DialogTitle>
          <DialogContent>
            <Typography>{t.confirmDeleteMsg}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                setDeleteConfirm({ open: false, id: null, type: null })
              }
            >
              {t.cancel}
            </Button>
            <Button variant="contained" color="error" onClick={doDeleteAccount}>
              {t.delete}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete all confirmation */}
        <Dialog
          open={deleteAllConfirm.open}
          onClose={() => setDeleteAllConfirm({ open: false, type: null })}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>{t.confirmDeleteAllTitle}</DialogTitle>
          <DialogContent>
            <Typography sx={{ mb: 2 }}>{t.confirmDeleteAllMsg}</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder={t.typeDeletePlaceholder}
              value={deleteAllInput}
              onChange={(e) => setDeleteAllInput(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setDeleteAllConfirm({ open: false, type: null })}
            >
              {t.cancel}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={deleteAllInput !== 'DELETE'}
              onClick={doDeleteAll}
            >
              {t.deleteAll}
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            severity={snack.severity}
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            sx={{ width: '100%' }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
}
